import React from "react";
import { CARD_CLASS } from "../../constants/dashboard";
import type { ProjectAggregate } from "../../utils/timesheetWeek";
import { formatHours } from "../../utils/timesheetWeek";
import SimpleTooltip from "../common/SimpleTooltip";

interface WeeklySummaryCardProps {
  totalHours: number;
  targetHours: number;
  projects: ProjectAggregate[];
  loading?: boolean;
}

const WeeklySummaryCard: React.FC<WeeklySummaryCardProps> = ({
  totalHours,
  targetHours,
  projects,
  loading = false,
}) => {
  return (
    <div className={`${CARD_CLASS} p-5`}>
      <div className="flex items-center gap-7 flex-wrap">
        <div className="flex-none">
          <div className="text-caption font-semibold text-text-tertiary mb-1">
            Logged this week
          </div>
          {loading ? (
            <div className="h-8 w-28 rounded-md bg-track animate-pulse" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-[32px] font-extrabold tracking-[-0.03em] leading-none text-text-primary">
                {formatHours(totalHours)}
              </span>
              <span className="text-body-md font-semibold text-text-tertiary">
                / {formatHours(targetHours)}
              </span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[240px]">
          <div className="flex h-3 rounded-full overflow-hidden bg-track">
            {projects.length === 0 ? (
              <div className="h-full w-full bg-track" />
            ) : (
              projects.map((p) => (
                <div
                  key={p.name}
                  style={{
                    width: `${Math.max(p.widthPct, p.hours > 0 ? 1 : 0)}%`,
                    background: p.color,
                  }}
                />
              ))
            )}
          </div>
          <div className="flex gap-[18px] flex-wrap mt-3">
            {projects.length === 0 ? (
              <span className="text-caption font-semibold text-text-tertiary">
                No tasks logged yet
              </span>
            ) : (
              projects.map((p) => (
                <SimpleTooltip key={p.name} label={`${p.name} · ${formatHours(p.hours)}`} side="top">
                  <span
                    className="flex items-center gap-[7px] text-caption font-semibold text-text-strong max-w-[200px]"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-[3px] flex-none"
                      style={{ background: p.color }}
                    />
                    <span className="truncate">{p.name}</span>
                    <span className="text-text-tertiary font-bold flex-none">
                      {formatHours(p.hours)}
                    </span>
                  </span>
                </SimpleTooltip>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklySummaryCard;
